import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '@/lib/api';
import { schemaDefinitionKeys } from '@/lib/schema-definition-query-keys';
import type { FieldToExpose } from '@/lib/api';
import { CompanyTemplateVariable } from '@reach/shared-types/template-variables';
import type { StripoMergeField } from './stripo/stripo.types';

const STANDARD_MERGE_FIELDS: StripoMergeField = {
  category: 'Standard Customer Fields',
  entries: [
    { label: 'First Name', value: '{{First Name}}' },
    { label: 'Last Name', value: '{{Last Name}}' },
    { label: 'Email', value: '{{Email}}' },
    { label: 'Phone', value: '{{Phone}}' },
  ],
};

const COMPANY_FIELD_LABELS: Record<CompanyTemplateVariable, string> = {
  [CompanyTemplateVariable.COMPANY_NAME]: 'Company Name',
  [CompanyTemplateVariable.COMPANY_WEBSITE]: 'Company Website',
  [CompanyTemplateVariable.COMPANY_PRIMARY_COLOR]: 'Company Primary Color',
  [CompanyTemplateVariable.COMPANY_LOGO_URL]: 'Company Logo URL',
  [CompanyTemplateVariable.COMPANY_EMAIL]: 'Company Email',
  [CompanyTemplateVariable.COMPANY_PHONE]: 'Company Phone',
  [CompanyTemplateVariable.COMPANY_EXTERNAL_ID]: 'Company External ID',
};

const BUSINESS_MERGE_FIELDS: StripoMergeField = {
  category: 'Business Fields',
  entries: Object.values(CompanyTemplateVariable).map(value => ({
    label: COMPANY_FIELD_LABELS[value],
    value,
  })),
};

function normalizeFieldToken(name: string): string {
  return name.replace(/[{}]/g, '');
}

function buildCustomMergeEntries(fields: FieldToExpose[]): StripoMergeField['entries'] {
  const seen = new Set<string>();
  return (
    fields
      .filter(field => field.exposure.includes('merge-fields'))
      .sort((a, b) => a.name.localeCompare(b.name))
      // Multiple schemas can expose a field with the same name; deduplicate after sort so first alphabetically wins.
      .reduce<StripoMergeField['entries']>((acc, field) => {
        const token = normalizeFieldToken(field.name);
        if (seen.has(token)) return acc;
        seen.add(token);
        acc.push({ label: field.name, value: `{{${token}}}` });
        return acc;
      }, [])
  );
}

function buildMergeFields(customEntries: StripoMergeField['entries']): StripoMergeField[] {
  const groups: StripoMergeField[] = [STANDARD_MERGE_FIELDS];
  if (customEntries.length > 0) {
    groups.push({ category: 'Custom Fields', entries: customEntries });
  }
  groups.push(BUSINESS_MERGE_FIELDS);
  return groups;
}

export interface TemplateMergeFields {
  mergeFields: StripoMergeField[];
  isLoading: boolean;
}

export function useTemplateMergeFields(): TemplateMergeFields {
  const { data: schemas, isLoading } = useQuery({
    queryKey: schemaDefinitionKeys.list(),
    queryFn: () => partnerApi.getSchemaDefinitions(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  const mergeFields = useMemo(() => {
    const rawFields = (schemas ?? []).flatMap(s => s.fields_to_expose ?? []);
    const customEntries = buildCustomMergeEntries(rawFields);
    return buildMergeFields(customEntries);
  }, [schemas]);

  return { mergeFields, isLoading };
}
